<script setup>
import { useI18n } from 'vue-i18n'
import { watch, reactive, computed, watchEffect } from 'vue'
import SlideStep from '@/components/slideModal/SlideStep.vue'
import { useSlideControls } from '@/composables/useSlideControls'
import AddSocialAccountButton from '@/components/AddSocialAccountButton.vue'
import { PowerBISlide } from '@/helpers/Slides'
import { BarChartOutlined } from '@ant-design/icons-vue'
import { useStore } from 'vuex'
import DurationSlideStep from '@/components/slideModal/DurationSlideStep.vue'
const props = defineProps({
  title: String,
  slideObj: PowerBISlide,
  groupId: String,
  disabled: Boolean,
  zoning: Boolean
})

const emit = defineEmits(['update:slideData'])
const { t } = useI18n()
const store = useStore()

const { slide, updateSlide, toggleDisabled, updateSlideData } = useSlideControls(emit, props.slideObj, PowerBISlide, {
  setMute: props.zoning,
  disableOnNewSlide: true
})

const state = reactive({
  socialPowerBIAccountId: slide.value.slideData.socialPowerBIAccountId,
  webUrl: slide.value.slideData.webUrl || ''
})

const isValid = computed(() => isValidPowerBIUrl(state.webUrl) && state.socialPowerBIAccountId)

const powerBiAccounts = computed(() => store.getters['social/socialPowerBIAccounts'].map(store.getters['social/toStandard']))

const isValidPowerBIUrl = (url) => {
  if (!url) return false

  const powerBIUrlPattern = /^https:\/\/app\.powerbi\.com\/(?:links\/[a-zA-Z0-9_-]+|(?:groups\/(?:me|[a-f0-9-]+)(?:\/apps\/[a-f0-9-]+)?\/(?:dashboards|reports)|datahub\/datasets\/[a-f0-9-]+))(?:\/[^?]+)?(?:\?[^#]+)?$/

  return powerBIUrlPattern.test(url)
}

const updateWebUrl = (event) => {
  const url = event.target.value
  state.webUrl = url
  updateSlideData({ ...slide.value.slideData, webUrl: url })
  updateSlide()
}

const updateAccount = (socialPowerBIAccountId) => {
  state.socialPowerBIAccountId = socialPowerBIAccountId
  updateSlideData({ ...slide.value.slideData, socialPowerBIAccountId })
  updateSlide()
}

watchEffect(() => {
  toggleDisabled(!isValid.value)
})

watchEffect(() => {
  if (powerBiAccounts.value?.length === 1 && !state.socialPowerBIAccountId) {
    state.socialPowerBIAccountId = powerBiAccounts.value[0].id
    updateSlideData({ ...slide.value.slideData, socialPowerBIAccountId: powerBiAccounts.value[0].id })
    updateSlide()
  }
})

watch(() => powerBiAccounts.value, (value, oldValue) => {
  if (value?.length - oldValue?.length === 1) {
    const newPowerBiAccount = value?.find(({ id }) => !oldValue.some(({ id: oldId }) => oldId === id))
    if (newPowerBiAccount) {
      state.socialPowerBIAccountId = newPowerBiAccount.id
    }
  }
})

</script>

<template>
  <div class="powerbi-slide">
    <DurationSlideStep
      :slide="slide"
      @update-slide="updateSlide"
    />
    <SlideStep
      :sub-title="$t('components.slides.powerBi.slideStep1.subTitle')"
      :title="$t('components.slides.powerBi.slideStep1.title')"
    >
      <template #extra>
        <AddSocialAccountButton :socials="['powerbi']" />
      </template>
      <a-select
        v-model:value="state.socialPowerBIAccountId"
        :placeholder="$t('components.slides.powerBi.slideStep1.accountSelectPlaceholder')"
        style="width: 100%"
        @change="updateAccount"
      >
        <a-select-opt-group v-if="powerBiAccounts.length > 0">
          <template #label>
            <span>
              <BarChartOutlined />
              Power BI
            </span>
          </template>
          <a-select-option
            v-for="account in powerBiAccounts"
            :key="account.id"
            :value="account.id"
          >
            <div>
              @ {{ account.name }}
            </div>
          </a-select-option>
        </a-select-opt-group>
      </a-select>
    </SlideStep>
    <SlideStep
      :title="t('components.slides.powerBi.slideStep2.title')"
      :sub-title="t('components.slides.powerBi.slideStep2.subTitle')"
    >
      <a-input
        v-model:value="state.webUrl"
        :placeholder="t('components.slides.powerBi.slideStep2.embedUrlPlaceholder')"
        :status="state.webUrl && !isValidPowerBIUrl(state.webUrl) ? 'error' : ''"
        @change="updateWebUrl"
      />
    </SlideStep>
  </div>
</template>

<style lang="less" scoped>

</style>
